import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import imageCompression from 'browser-image-compression';
import t from 'locales/en/main.json';
import { isString } from 'lodash';
import { setErrorToast } from 'store/toast';

import ImageEditor from '../../containers/CarPictures/Components/ImageEditor';

import { Input } from './styles';

const isImageSizeAccepted = (width: number, height: number) => {
  return Math.floor((height / width) * 10) === 7;
};

interface IProps {
  onChooseImage: (file: File) => void;
  isOpenTakeImage: boolean;
  allowedTypes?: string[];
  isRotate: boolean;
  isEditImage?: boolean;
}

const imageTypes = ['image/jpeg', 'image/png', 'image/jpg'];

export const fileDocumentType = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/*',
  '.doc',
  'application/msword',
  '.docx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const mediaType = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'video/mp4',
  'video/quicktime',
  'video/*',
  'audio/*',
];

const TakeImage: React.FC<IProps> = ({
  onChooseImage,
  isOpenTakeImage,
  allowedTypes,
  isRotate,
  isEditImage,
}) => {
  const dispatch = useDispatch();
  const [isShowModal, onToggleIsShowModal] = useState(false);
  const refTakeImage = useRef<HTMLInputElement>(null);
  const [image, onChangeImage] = useState<string | undefined>();
  const [imageFile, onChangeImageFile] = useState<Blob | undefined>();

  const handleUploadImage = () => {
    if (refTakeImage?.current) {
      refTakeImage?.current.click();
    }
  };

  useEffect(() => {
    isOpenTakeImage && handleUploadImage();
  }, [isOpenTakeImage]);

  const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event?.target?.files?.[0];

    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1800,
      useWebWorker: true,
    };

    if (file && file.type.includes('image')) {
      file = await imageCompression(file, options);
    }

    if (file) {
      if (allowedTypes) {
        if (isString(file.type) && !allowedTypes.includes(file.type)) {
          return dispatch(
            setErrorToast({
              title: '',
              message: `${t.image_picker.image_size_error_type} ${allowedTypes.join(', ')}`,
              variant: 'error',
            }),
          );
        } else if (isRotate && isString(file.type) && imageTypes.includes(file.type)) {
          let reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = function (e) {
            const image = new Image();
            image.src = `${e?.target?.result}`;
            image.onload = () => {
              if (isImageSizeAccepted(image.width, image.height)) {
                logicImage(file as File);
              } else {
                dispatch(
                  setErrorToast({
                    title: t.image_picker.image_size_error_title,
                    message: t.image_picker.image_size_error,
                    variant: 'error',
                  }),
                );
              }
            };
          };
        } else {
          logicImage(file);
        }
      } else {
        logicImage(file);
      }
    }
  };

  const logicImage = (file: File) => {
    if (isEditImage && file.type.includes('image')) {
      onToggleIsShowModal(true);
      blobToBase64(file).then((res) => {
        onChangeImage(res as string);
      });
      onChangeImageFile(file as File);
    } else {
      onChooseImage(file);
    }
  };

  const blobToBase64 = (blob: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  };
  return (
    <>
      <Input
        type="file"
        onChange={onChangeFile}
        accept={`${allowedTypes && allowedTypes.join(', ')}`}
        ref={refTakeImage}
      />
      {isEditImage && isShowModal && image && (
        <ImageEditor
          image={image}
          onCloseModal={() => {
            onToggleIsShowModal(false);
            onChooseImage(imageFile as File);
          }}
          onEditedImage={(e) => {
            onChooseImage(e);
            onToggleIsShowModal(false);
          }}
        />
      )}
    </>
  );
};

export { TakeImage };
